import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { formatNoteHTML } from '../../../utils/noteTypeMapper';

/**
 * Parse note text into table rows by splitting on colon
 * @param {string} noteText - The note text to parse
 * @returns {Array} Array of {label, value} objects or null if no colons found
 */
const parseNoteToTable = (noteText) => {
  if (!noteText) return null;
  
  // Split by newlines to get individual lines
  const lines = noteText.split('\n').filter(line => line.trim());
  
  // Check if lines contain colons - if not, return null to display as plain text
  const hasColons = lines.some(line => line.includes(':'));
  if (!hasColons) return null;
  
  // Parse each line that contains a colon
  const rows = lines
    .filter(line => line.includes(':'))
    .map(line => {
      const colonIndex = line.indexOf(':');
      const label = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      return { label, value };
    })
    .filter(row => row.label && row.value); // Only include rows with both label and value
  
  return rows.length > 0 ? rows : null;
};

/**
 * StructuredNotesDisplay - Displays PA/ST structured notes organized by indication with table layout
 */
const StructuredNotesDisplay = ({ notes, title, description }) => {
  if (!notes || notes.length === 0) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground text-center">
          Detailed notes not provided by plan
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {notes.map((note, index) => {
        const formattedNote = formatNoteHTML(note.Note);
        const tableRows = parseNoteToTable(formattedNote);
        
        return (
          <Card key={index} className="border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold">{note.Indication?.name || 'Unknown Indication'}</CardTitle>
            </CardHeader>
            <CardContent>
              {tableRows ? (
                // Display as table if colon-delimited format detected
                <div className="bg-muted rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <tbody>
                      {tableRows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-border last:border-b-0">
                          <td className="font-semibold text-foreground p-3 align-top w-1/3 bg-muted/50">
                            {row.label}
                          </td>
                          <td className="text-foreground p-3 align-top">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Fallback to plain text display if no colon format
                <div className="bg-muted rounded-lg p-3">
                  <pre className="whitespace-pre-wrap text-xs font-sans text-foreground">
                    {formattedNote}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default StructuredNotesDisplay;
